import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

/** The four plan types on step 2, with what each one bills. */
const PLAN_TYPES: { name: string; body: string }[] = [
  {
    name: 'Bundle',
    body: 'A pre-configured package. Pick one; only bundles sold for the billing cycle you chose are listed.',
  },
  {
    name: 'Modules',
    body: 'Pick individual modules. Again only modules sold for the chosen billing cycle are listed.',
  },
  {
    name: 'Custom',
    body: 'Manual pricing: type a flat recurring Base price and nothing else is needed from the catalog.',
  },
  {
    name: 'Usage-based only',
    body: 'No bundle, module or base fee. It bills only the meters you attach on the next step, after each meter’s included allowance.',
  },
];

export function CreateSubscriptionGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Create a subscription — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Create a subscription</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Create a subscription, <em>start to first invoice</em>.
        </h1>
        <p className="dp-oneliner">
          A four-step wizard turns a client, a plan and a start date into a subscription and its
          first invoice. This guide walks the steps in order, including the choices people most
          often get wrong: a backdated start, an excluded meter and a custom price.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Where to start
        </h2>
        <UiPath
          steps={['Subscriptions', 'New subscription']}
          note="Or open a client, go to its Subscriptions tab and choose New Subscription. Opened from a client, the client is fixed."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Before you begin, the client needs a <strong>region</strong>. Pricing and included
          allowances are resolved by region, and the wizard stops on step 1 with an alert if the
          client has none. Assign one in Edit client first. The region is not chosen in the wizard:
          it is the client&rsquo;s.
        </p>
        <Figure
          title="The wizard, start to first invoice"
          caption="Four steps collect the choices; creating the subscription raises the first invoice."
        >
          <Flow
            steps={[
              {
                title: 'Basic info',
                text: 'Client, currency, billing cycle and start date.',
              },
              {
                title: 'Plan and pricing',
                text: 'Bundle, modules, custom base price or usage only.',
              },
              {
                title: 'Usage components',
                text: 'Attach, price or exclude each meter.',
              },
              {
                title: 'Settings',
                text: 'End date, billing schedule, discounts and notes.',
              },
              {
                title: 'First invoice',
                text: 'Create subscription saves it and generates the start invoice.',
                tone: 'accent',
              },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Step 1: Basic info
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Client</div>
            <div className="dp-field-v">
              Search and select. Changing the client clears the currency, because the options come
              from that client&rsquo;s region.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Currency</div>
            <div className="dp-field-v">
              Limited to the currencies the client&rsquo;s region supports. See{' '}
              <Link to="/docs/guides/multi-currency">Multi-currency</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Billing Cycle</div>
            <div className="dp-field-v">
              Monthly, Quarterly or Yearly. It filters the bundles and modules offered on the next
              step.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Start Date</div>
            <div className="dp-field-v">
              Required. A date in the past is allowed and starts the backdating flow in Step 4 (section 05).
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          If the client has a manual or API usage capture mode set, a callout names it here, so you
          know how metered usage will reach Revvly for this client.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Step 2: Plan &amp; pricing
        </h2>
        <ol className="qs-steps">
          {PLAN_TYPES.map((p, i) => (
            <li className="qs-step" key={p.name}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{p.name}</div>
                <div className="qs-step-detail">{p.body}</div>
              </div>
            </li>
          ))}
        </ol>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The <strong>Pricing Summary</strong> shows the calculated Base Price. For a bundle or
          modules, <strong>Override</strong> replaces it with a custom base price for this client
          only (<strong>Use calculated</strong> undoes it). The list price stays visible beside it and
          the change is recorded in the price-change history. A custom subscription is the same idea
          taken to its end: only a base price, no catalog plan, and a price of 0 is accepted as a
          deliberate free arrangement. See{' '}
          <Link to="/docs/guides/override-pricing">Override a price</Link>.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Starting in the past with a plan you no longer sell? Tick{' '}
          <strong>Show retired plans</strong> and pick the old version. You must give a reason,
          which is recorded on the pin history. See{' '}
          <Link to="/docs/guides/legacy-pricing">Legacy pricing</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Step 3: Usage components (your meters)
        </h2>
        <p className="dp-plain">
          The step is titled Usage components in the dashboard; each row is a meter. Meters included
          by the plan are ticked automatically and badged <strong>From plan</strong>. Tick any other
          meter to attach it. Each attached meter has:
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Override price &amp; allowance</div>
            <div className="dp-field-v">
              Changes the included allowance and the price for this client only. The override asks
              for what the pricing model bills on: one rate for a flat rate, one rate per tier for a
              tiered meter, one fee per step for a stairstep meter. Use plan defaults undoes it.
              A meter with no price in the chosen currency is badged <strong>No price set</strong>
              and blocks Continue until you price it or remove it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Starting quantity</div>
            <div className="dp-field-v">An optional opening quantity, whole numbers only.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Billing timing</div>
            <div className="dp-field-v">
              Product default, Advance or Arrears for this client. See{' '}
              <Link to="/docs/guides/billing-timing">Advance vs. arrears billing</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fee at start</div>
            <div className="dp-field-v">
              What the meter adds to the start invoice, with its breakdown on hover.
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>To leave out a meter the plan would include</strong>, untick it. It is badged{' '}
          <strong>Excluded</strong> and is left off this subscription only; tick it again to bring
          it back. The catalog plan itself is untouched. For how usage is then fed in, see{' '}
          <Link to="/docs/guides/metered-usage">Metered usage</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Step 4: Settings, and the catch-up choice
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Ends</div>
            <div className="dp-field-v">
              <strong>Open-ended</strong> renews automatically each term until someone cancels.{' '}
              <strong>Ends on</strong> takes an end date and turns automatic renewal off.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Billing schedule</div>
            <div className="dp-field-v">
              Standard, or a prepaid term or deferred activation. Only shown when the Billing
              schedule feature is on for your workspace. See{' '}
              <Link to="/docs/guides/billing-schedules">Billing schedules</Link>. A prepaid term
              also takes a prepay incentive (%) and what it applies to: the base price (the
              default), the components billed ahead, or both. A deferred activation has no
              backdating.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Add discounts</div>
            <div className="dp-field-v">
              In the recap, queue discounts that are created with the subscription. See{' '}
              <Link to="/docs/guides/discounting">Discounting</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Notes &amp; Comments</div>
            <div className="dp-field-v">Free text kept on the subscription.</div>
          </div>
        </div>

        <h3 style={{ marginTop: 28 }}>A start date in the past</h3>
        <p className="dp-plain">
          If at least one whole term has already closed since the start date, step 1 warns you and
          step 4 leads with a decision you must answer before you can create the subscription.
          Nothing is pre-selected, because an unticked box reads the same as never having seen the
          question.
        </p>
        <Figure
          title="Choosing what to do with missed terms"
          caption="You must answer before creating, because nothing is pre-selected."
        >
          <Flow
            steps={[
              {
                title: 'Start date in the past',
                text: 'At least one whole term has already closed.',
                tone: 'warn',
              },
              {
                fork: [
                  {
                    title: 'Generate missed invoices',
                    text: 'One invoice per elapsed term. Renewal continues from today.',
                    tone: 'accent',
                  },
                  {
                    title: 'Skip: start invoice only',
                    text: 'Elapsed terms are never billed and record no revenue.',
                    tone: 'muted',
                  },
                ],
              },
            ]}
          />
        </Figure>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Generate the missed invoices</div>
            <div className="dp-field-v">
              One invoice per elapsed term, dated to when it would have been issued, and the
              subscription is advanced so renewal continues from today. For an open-ended
              subscription that is every term between the start date and today. With an end date it
              is every term up to the end date, and terms that have not yet elapsed are issued as
              unpaid, which can create future-dated invoices. For arrears-only billing each invoice
              is dated to the end of the term it covers, and the term still running is invoiced when
              it ends.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Skip: start invoice only</div>
            <div className="dp-field-v">
              Only the initial invoice is created. The elapsed terms are never billed and no revenue
              is recorded for them.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Per-invoice review</div>
            <div className="dp-field-v">
              When you generate, each invoice is listed. Terms that have fully elapsed start ticked
              as <strong>Paid</strong> with a <strong>Paid on</strong> date you can change; untick
              any that were not paid. Where meters are billed you can adjust the quantity billed in
              each term.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          What happens when you create it
        </h2>
        <p className="dp-plain">
          Choose <strong>Create subscription</strong> on the last step. Revvly saves the
          subscription, applies any queued discounts, and generates the start invoice, or the
          backdated series if you chose it. Warnings, such as a discount that could not be applied,
          appear as toasts and the subscription is still created.
        </p>
        <ul className="dp-connects">
          <li>
            The recap shows the recurring total and, when arrears meters are attached, a{' '}
            <strong>Start invoice</strong> line that excludes them: an arrears meter bills for the
            period that has just closed, so it first appears on the first renewal invoice.
          </li>
          <li>
            A subscription must bill something: a bundle, modules, a meter or a base price. The
            server refuses an empty one.
          </li>
          <li>
            Renewals continue from there. See <Link to="/docs/guides/renewals">Renewals</Link> and{' '}
            <Link to="/docs/guides/mid-cycle-changes">Mid-cycle changes</Link>.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Settings that sit outside the wizard
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Next invoice number</div>
            <div className="dp-field-v">
              Set it before the first invoice when a client already has invoice history elsewhere.
              Enter the number the next invoice should carry (a client at invoice 14 gets 15). It
              applies to the next invoice generated and then clears; numbering never goes backwards.
              Leave it empty to continue automatically.
              <UiPath steps={['Clients', 'open a client', 'Edit client', 'Next Invoice Number']} />
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Advance renewal invoicing</div>
            <div className="dp-field-v">
              Days before a renewal to generate the invoice, set per billing period (0 to 365). The
              workspace default is under Settings, General, Renewal Invoice Timing, and a client can
              override each period under Edit client, Renewal Invoice Timing; blank means use the
              default and 0 switches advance invoicing off for that period. It applies to the
              recurring base charge only: usage is always billed at renewal.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Change the start date later</div>
            <div className="dp-field-v">
              Open the client, then the Subscriptions tab, open the subscription and choose Edit,
              then change Start Date. A <strong>Start date change</strong> section appears. Left
              alone, only the date is stored and the end date, next renewal date and invoices stay
              as they are. Switch on <strong>Recompute schedule and regenerate invoices</strong> to
              re-anchor the dates and rebuild the series, and choose how the existing invoices are
              handled, as listed below. Regenerating cannot be undone.
              <UiPath steps={['Clients', 'open a client', 'Subscriptions', 'Edit Subscription']} />
            </div>
          </div>
        </div>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Delete and rebuild all invoices</div>
            <div className="dp-field-v">
              Every invoice for the subscription is deleted and rebuilt from the new start date.
              Paid status is re-derived from whether each period has elapsed, so manual payment
              dates on the old invoices are lost. This is the default.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Keep paid invoices, redo the rest</div>
            <div className="dp-field-v">
              Paid invoices are kept and only the unpaid ones are rebuilt. A period already paid is
              never billed twice.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Void old invoices, issue a fresh set</div>
            <div className="dp-field-v">
              Existing invoices are marked cancelled and kept as an audit trail, and a fresh series
              is issued from the new start date.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
